import React, { useState, useEffect, useMemo } from 'react';
import { Box, Container, Grid, Typography, AppBar, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { useSearchParams } from 'react-router';
import { QlikVizPanel } from '../../qlik-viz-panel/QlikVizPanel';
import FilterDropdown from '../../filter-dropdown/FilterDropDown';
import AppTopBar from '../../app-bar/AppBar';
import Footer from '../../footer/Footer';
import { PAGES, INDUSTRY_FIELD, COMPANY_ANNUAL_REV_FIELD } from '../../../constants';
import { ExtraControlsConfig } from '../../../types';
import { applySelectionByFieldName } from '../../../services/qlikSelectionService';
import { QLIK_APP_REF } from '../../../constants';

const CostsBreakdown: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const page = useMemo(() => PAGES['costs-breakdown'].contents ?? {}, []);
  const [searchParams] = useSearchParams();
  const [initialized, setInitialized] = useState(false);

  const [pageTitle] = useState(page.title.text || '');
  const [pageSubtitle] = useState(page.sub.text || '');

  const noteChart1 = PAGES['costs-breakdown'].contents.noteChart1.text.split(/<br\s*\/?>/i);
  const noteChart2 = PAGES['costs-breakdown'].contents.noteChart2.text.split(/<br\s*\/?>/i);

  useEffect(() => {
    initFromUrlParams();
  }, []);

  const initFromUrlParams = async () => {
    try {
      // Get URL parameters
      const industry = searchParams.get('industry');
      const compRev = searchParams.get('compRev');
      const dateRange = searchParams.get('dateRange');

      // Apply selections from URL parameters
      if (industry) {
        await applySelectionByFieldName(QLIK_APP_REF, INDUSTRY_FIELD, [industry]);
      }
      if (compRev) {
        await applySelectionByFieldName(QLIK_APP_REF, COMPANY_ANNUAL_REV_FIELD, [compRev]);
      }
      if (dateRange) {
        await applySelectionByFieldName(QLIK_APP_REF, 'Date Range', [dateRange]);
      }

      setInitialized(true);
    } catch (error) {
      console.error('Error initializing from URL params:', error);
      setInitialized(true);
    }
  };

  // Filter elements for desktop sticky toolbar
  const filterElements = (
    <>
      <FilterDropdown
        formName="Page Filters"
        fieldName={INDUSTRY_FIELD}
        fieldLabel="Industry"
        multiSelect={false}
        qlikSort="qSortByAscii"
      />
      <FilterDropdown
        formName="Page Filters"
        fieldName={COMPANY_ANNUAL_REV_FIELD}
        fieldLabel="Company Revenue"
        multiSelect={true}
        qlikSort="qSortByLoadOrder"
        sortExpression="SortValue"
      />
      <FilterDropdown
        formName="Page Filters"
        fieldName="Date Range"
        fieldLabel="Date Range"
        multiSelect={false}
        qlikSort="qSortByLoadOrder"
        removeValues={['Rolling 12 Months', 'Current Year', 'Last Complete Year']}
      />
    </>
  );

  // Extra controls config
  const extraControls: ExtraControlsConfig = {
    startOver: { enable: true },
    lastUpdate: { enable: true, componentLocation: 'nav' },
  };

  return (
    <>
      <AppTopBar
        extraControls={extraControls}
        filterElements={filterElements}
      />

      {/* Sticky Filter Bar for Desktop */}
      {!isMobile && (
        <AppBar
          position="sticky"
          sx={{
            top: 65, // Height of main AppBar
            zIndex: (theme) => theme.zIndex.appBar + 1,
            backgroundColor: 'white',
            boxShadow: 1,
            borderBottom: 1,
            borderColor: 'divider',
            ml: '57px', // Align with collapsed drawer
            width: 'calc(100% - 57px)',
          }}
        >
          <Toolbar
            sx={{
              pt: 2,
              minHeight: '50px !important',
            }}
          >
            <Grid container spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
              {/* Left side - Industry filter */}
              <Grid size={{ md: 3}}>
                <FilterDropdown
                  formName="Page Filters"
                  fieldName={INDUSTRY_FIELD}
                  fieldLabel="Industry"
                  multiSelect={false}
                  qlikSort="qSortByAscii"
                />
              </Grid>

              {/* Right side - Company Revenue filter */}
              <Grid size={{ md: 3}} offset={{ md: 'auto'}}>
                <FilterDropdown
                  formName="Page Filters"
                  fieldName={COMPANY_ANNUAL_REV_FIELD}
                  fieldLabel="Company Revenue"
                  multiSelect={true}
                  qlikSort="qSortByLoadOrder"
                  sortExpression="SortValue"
                />
              </Grid>

              {/* Right side - Date Range filter */}
              <Grid size={{ md: 3}} offset={{ md: 0}}>
                <FilterDropdown
                  formName="Page Filters"
                  fieldName="Date Range"
                  fieldLabel="Date Range"
                  multiSelect={false}
                  qlikSort="qSortByLoadOrder"
                  removeValues={['Rolling 12 Months', 'Current Year', 'Last Complete Year']}
                />
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      )}

      {/* if enabled, has to be moved in the Grid */}
      
      {/* Page Content */}
      {/* <Box
        sx={{
          ml: { xs: 0, md: '57px' },
          width: { xs: '100%', md: 'calc(100% - 57px)' },
          minHeight: 'calc(100vh - 128px)',
          py: 3,
        }}
      > */}
        {/* <Container maxWidth="lg"> */}
          {/* Page Title & Subtitle */}
          {/* <Box sx={{ mb: 4 }}> */}
            {/* Mobile Title */}
            {/* <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 2 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                {pageTitle}
              </Typography>
              <Box
                sx={{
                  width: 100,
                  height: 3,
                  backgroundColor: 'primary.main',
                  margin: '0 auto',
                }}
              />
            </Box> */}

            {/* Desktop Title */}
            {/* TODO: check if this should be hided in any case */}
            {/* <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                {pageTitle}
              </Typography>
              <Box
                sx={{
                  borderLeft: 3,
                  borderColor: 'primary.main',
                  pl: 2,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                  }}
                >
                  {pageSubtitle}
                </Typography>
              </Box>
            </Box>
          </Box> */}

          {/* Charts Grid with Equal Height */}
          <Grid 
            container
            spacing={3}
            component="main"
            sx={{
                py: 2,
                px: { xs: 2, sm: 2, md: 2 },
                // Add margin for the left drawer on desktop (md and up)
                // The drawer is 57px when collapsed, 257px when expanded
                ml: { xs: 0, md: '57px' }, // Collapsed drawer width
                my: 4,
                transition: 'margin-left 0.3s',
            }}
        >
            {/* Total Incident Costs */}
            <Grid size={{ xs: 12, md: 6 }}>
              <QlikVizPanel
                qlikVizName="costs-bd-total-claim-pie-1"
                legendName="costs-bd-total-claim-pie-1"
                addElToExport="totalIncCostsNote"
                expand={true}
                flexColumn={true}
                enableInteractions={false}
                equalHeight={true}
              />
            </Grid>
            <Grid size={{ xs: 12 }} sx={{display: { xs: 'block', md: 'none' },}}>
              {/* Mobile Note */}
              <Typography
                id="totalIncCostsNote"
                variant="caption"
                sx={{
                  display: { xs: 'block', md: 'none' },
                  fontStyle: 'italic',
                  color: 'text.primary'
                }}
              >
                {noteChart1.map((line: any, idx: number) => (
                <span key={idx}>
                    {line}
                    {idx < noteChart1.length - 1 && <br />}
                </span>
                ))}
              </Typography>
            </Grid>

            {/* Average Paid Incident */}
            <Grid size={{ xs: 12, md: 6 }}>
              <QlikVizPanel
                qlikVizName="costs-bd-avg-paid"
                // legendName="costs-bd-avg-paid-legend"
                legendName="costs-bd-avg-paid"
                flexColumn={true}
                expand={true}
                enableInteractions={false}
                addAsterisk={true}
                equalHeight={true}
              />
            </Grid>
            <Grid size={{ xs: 12 }} sx={{display: { xs: 'block', md: 'none' },}}>
              {/* Mobile Note */}
              <Typography
                variant="caption"
                sx={{
                  display: { xs: 'block', md: 'none' },
                  fontStyle: 'italic',
                  color: 'text.primary'
                }}
              >
                {noteChart2.map((line: any, idx: number) => (
                <span key={idx}>
                    {line}
                    {idx < noteChart2.length - 1 && <br />}
                </span>
                ))}
              </Typography>
            </Grid>
          </Grid>

          {/* Desktop Notes */}
          <Grid 
            container
            spacing={3}
            sx={{ 
              display: { xs: 'none', md: 'flex' },
              mb: 4,
              px: { xs: 2, sm: 2, md: 2 },
              ml: { xs: 0, md: '57px' }
          }}
            >
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                id="totalIncCostsNote"
                variant="caption"
                sx={{
                  fontStyle: 'italic',
                  display: 'block',
                  color: 'text.primary'
                }}
              >
                {noteChart1.map((line: any, idx: number) => (
                <span key={idx}>
                    {line}
                    {idx < noteChart1.length - 1 && <br />}
                </span>
                ))}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="caption"
                sx={{
                  fontStyle: 'italic',
                  display: 'block',
                  color: 'text.primary'
                }}
              >
                {noteChart2.map((line: any, idx: number) => (
                <span key={idx}>
                    {line}
                    {idx < noteChart2.length - 1 && <br />}
                </span>
                ))}
              </Typography>
            </Grid>
          </Grid>
        {/* </Container> */}
      {/* </Box> */}

      {/* Footer */}
      <Footer componentLocation="dashboard" />
    </>
  );
};

export default CostsBreakdown;
